import type { ContextBlock, PipelineStage, Span } from '../types';
import { draftText } from '../draft-text';
import { stripCommentsAndBlankLines } from '../../parsing/minify';

export const b1CommentWhitespaceStripping: PipelineStage = {
  id: 'B1',
  group: 'B',
  label: 'Comment and whitespace stripping',
  description: 'Strip comments and blank lines and collapse indentation in context code before sending.',
  impact: 'Medium',
  defaultEnabled: true,
  implemented: true,
  params: {
    keepDocstrings: { type: 'boolean', default: true, label: 'Keep docstrings' },
  },

  async run(draft, params, ctx) {
    const tokensBefore = ctx.estimateTokens(draftText(draft));
    const keepDocstrings = params.keepDocstrings !== false;
    const langByPath = new Map(draft.openFiles.map((f) => [f.path, f.languageId]));

    const newBlocks: ContextBlock[] = [];
    let changedCount = 0;
    let warning: string | undefined;

    for (const block of draft.contextBlocks) {
      if (!block.id.startsWith('file:')) {
        newBlocks.push(block);
        continue;
      }
      const path = block.id.slice('file:'.length);
      const languageId = langByPath.get(path);
      if (!languageId) {
        newBlocks.push(block);
        continue;
      }
      try {
        // reportKeptSpans tags the docstrings that survived stripping so B4 can
        // find them later; the offsets are in the stripped output's own space.
        const { text, keptSpans } = await stripCommentsAndBlankLines(block.content, languageId, {
          keepDocstrings,
          reportKeptSpans: true,
        });
        newBlocks.push({ ...block, content: text, spans: keptSpans.map((s): Span => ({ kind: s.kind, start: s.start, end: s.end })) });
        changedCount++;
      } catch (err) {
        newBlocks.push(block); // fail open — leave this one block untouched
        warning = err instanceof Error ? err.message : String(err);
      }
    }

    const newDraft = { ...draft, contextBlocks: newBlocks };
    const tokensAfter = ctx.estimateTokens(draftText(newDraft));

    return {
      draft: newDraft,
      report: {
        stageId: 'B1',
        tokensBefore,
        tokensAfter,
        tokensSaved: Math.max(0, tokensBefore - tokensAfter),
        notes: [changedCount > 0 ? `Stripped comments/blank lines from ${changedCount} file block(s).` : 'No parseable file blocks to strip.'],
      },
      warning,
    };
  },
};
